/**
 * HTTP-клиент входа. Контракт — маршруты `/api/auth/*` демона (§6.6, §9), не
 * `@screener/protocol`: сессия выражается транспортом (cookie, коды HTTP и
 * закрытия websocket), а не сообщением IPC-контракта.
 */

import { isRecord } from "./guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "./request-timeout";

const AUTH_BASE = "/api/auth";

export interface LoginCredentials {
  login: string;
  password: string;
}

export type AccessPlan = "free" | "trader" | "pro";

export type AccessSource = "subscription" | "team" | "trial" | "unlimited" | "billing_off" | "none";

/**
 * Что даёт тариф: биржи (`"all"` или перечень), пометки ММ с подходами и
 * библиотекой, звук с уведомлениями, число открытых карточек (`null` — без
 * ограничения). Поля — как в `GET /api/auth/me` (snake_case).
 */
export interface PlanFeatures {
  exchanges: "all" | string[];
  mm_approaches_library: boolean;
  sound_notify: boolean;
  open_cards: number | null;
}

/** Тариф из `plans[]` ответа `me`: по возрастанию цены. */
export interface PlanOffer {
  plan: AccessPlan;
  month_usd: string;
  features: PlanFeatures;
}

/**
 * Доступ по тарифу, как его считает демон (`access` в `GET /api/auth/me`,
 * stage9-billing-access, Д7). Поля — как в ответе демона (snake_case).
 */
export interface Access {
  /**
   * Состав тарифа (stage9-billing-gating, Д10). Старый демон поля не шлёт, и
   * поле чужой формы отбрасывается: тогда доступ считается полным (`pro`).
   */
  features?: PlanFeatures;
  plan: AccessPlan;
  source: AccessSource;
  until_ms: number | null;
  in_grace: boolean;
  billing_enabled: boolean;
}

/** Старый демон без поля `access`: биллинга нет, у всех полный доступ. */
const ACCESS_WITHOUT_BILLING: Access = {
  plan: "pro",
  source: "billing_off",
  until_ms: null,
  in_grace: false,
  billing_enabled: false,
};

export interface CurrentUser {
  login: string;
  /** Право выдавать приглашения (§104 п. 5); пункт меню показывается ещё и по флагу `invite`. */
  can_invite: boolean;
  /**
   * Учётная запись владельца (`owner.login`, §130 п. 5): удалить её из
   * интерфейса нельзя. Старый демон поля не шлёт — тогда `false`: удаление
   * владельца он всё равно отклонит.
   */
  is_owner: boolean;
  /**
   * Тариф и срок доступа. Старый демон поля не шлёт — тогда биллинг выключен.
   * `null` — поле пришло, но формы, которой клиент не знает (например, новый
   * источник после выкатки демона): остальной `me` от этого не отвергается.
   */
  access: Access | null;
  /** Состав тарифов по возрастанию цены; старый демон поля не шлёт — список пуст. */
  plans: PlanOffer[];
}

/**
 * Три исхода, не два: недоступность демона (сеть, `5xx`, нечитаемое или
 * неверной формы тело) — не то же самое, что отсутствие сессии (`401`).
 * Смешение этих исходов выбрасывает вошедшего человека на форму, где вход
 * тоже не работает (design review stage5-auth-sessions, пункт 2).
 */
export type CurrentUserResult =
  | { status: "authenticated"; user: CurrentUser }
  | { status: "anonymous" }
  | { status: "unavailable" };

function isAccessPlan(value: unknown): value is AccessPlan {
  return value === "free" || value === "trader" || value === "pro";
}

function isAccessSource(value: unknown): value is AccessSource {
  return (
    value === "subscription" ||
    value === "team" ||
    value === "trial" ||
    value === "unlimited" ||
    value === "billing_off" ||
    value === "none"
  );
}

function parsePlanFeatures(value: unknown): PlanFeatures | null {
  if (!isRecord(value)) {
    return null;
  }
  const { exchanges, mm_approaches_library, sound_notify, open_cards } = value;
  if (typeof mm_approaches_library !== "boolean" || typeof sound_notify !== "boolean") {
    return null;
  }
  if (open_cards !== null && (typeof open_cards !== "number" || !Number.isFinite(open_cards))) {
    return null;
  }
  if (exchanges === "all") {
    return { exchanges, mm_approaches_library, sound_notify, open_cards };
  }
  if (Array.isArray(exchanges) && exchanges.every((entry) => typeof entry === "string")) {
    return {
      exchanges: exchanges.filter((entry) => typeof entry === "string"),
      mm_approaches_library,
      sound_notify,
      open_cards,
    };
  }
  return null;
}

function parsePlans(value: unknown): PlanOffer[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const plans: PlanOffer[] = [];
  for (const entry of value) {
    if (!isRecord(entry) || !isAccessPlan(entry.plan) || typeof entry.month_usd !== "string") {
      continue;
    }
    const features = parsePlanFeatures(entry.features);
    if (features !== null) {
      plans.push({ plan: entry.plan, month_usd: entry.month_usd, features });
    }
  }
  return plans;
}

function parseAccess(value: unknown): Access | null {
  if (
    typeof value !== "object" ||
    value === null ||
    !("plan" in value) ||
    !isAccessPlan(value.plan) ||
    !("source" in value) ||
    !isAccessSource(value.source) ||
    !("until_ms" in value) ||
    (value.until_ms !== null && typeof value.until_ms !== "number") ||
    !("in_grace" in value) ||
    typeof value.in_grace !== "boolean" ||
    !("billing_enabled" in value) ||
    typeof value.billing_enabled !== "boolean"
  ) {
    return null;
  }
  const features = "features" in value ? parsePlanFeatures(value.features) : null;
  return {
    ...(features === null ? {} : { features }),
    plan: value.plan,
    source: value.source,
    until_ms: value.until_ms,
    in_grace: value.in_grace,
    billing_enabled: value.billing_enabled,
  };
}

/**
 * Разбор ответа `GET /api/auth/me`. `is_owner` может отсутствовать (демон
 * старше stage9-account-delete) — это `false`, а не недоступность; поле не
 * булева типа — чужая форма ответа.
 */
function parseCurrentUser(value: unknown): CurrentUser | null {
  if (
    typeof value !== "object" ||
    value === null ||
    !("login" in value) ||
    typeof value.login !== "string" ||
    !("can_invite" in value) ||
    typeof value.can_invite !== "boolean"
  ) {
    return null;
  }
  let isOwner = false;
  if ("is_owner" in value) {
    if (typeof value.is_owner !== "boolean") {
      return null;
    }
    isOwner = value.is_owner;
  }
  const access = "access" in value ? parseAccess(value.access) : ACCESS_WITHOUT_BILLING;
  const plans = "plans" in value ? parsePlans(value.plans) : [];
  return { login: value.login, can_invite: value.can_invite, is_owner: isOwner, access, plans };
}

/**
 * Действительна ли сессия у клиента. Различает отсутствие сессии (`401`) от
 * недоступности демона: сетевая ошибка, `5xx` или тело ответа, которое не
 * разбирается как `CurrentUser` (например `index.html` на рассинхроне
 * статики и демона), — тоже недоступность, а не отсутствие сессии.
 */
export async function fetchCurrentUser(): Promise<CurrentUserResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/me`, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "anonymous" };
  }
  if (!response.ok) {
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    const user = parseCurrentUser(body);
    if (user === null) {
      return { status: "unavailable" };
    }
    return { status: "authenticated", user };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * Исходы входа. Демон не различает причину при неверных данных (§6.6):
 * несуществующий логин и неверный пароль дают один и тот же исход
 * `invalid-credentials`. Отклонение по частоте попыток (`429`, §6.6 1.15)
 * различается отдельно: смешение с неверными данными заставляет человека
 * подбирать пароль дальше, хотя демон его уже не проверяет (web-login
 * «Упёршемуся в предел попыток сказано, что это предел»).
 *
 * Недоступность демона (сеть, `5xx`, неожиданный статус) — четвёртый исход,
 * не «невезучая» неудача входа: по той же причине, по которой
 * `fetchCurrentUser` различает `anonymous` и `unavailable`, а не сваливает их
 * в одно. Смешение с `invalid-credentials` читается человеком как «я забыл
 * пароль» и ведёт к подбору того, что демон в этот момент вообще не
 * проверяет.
 */
export type LoginResult =
  | "success"
  | "invalid-credentials"
  | "rate-limited"
  | "unavailable"
  | EmailUnconfirmed;

/**
 * Верный пароль, но почта не подтверждена (`403 {"reason": "email_unconfirmed"}`,
 * stage9-email-confirmation, Р8): сессии нет, `email` — маска адреса, на который
 * ушло письмо.
 */
export interface EmailUnconfirmed {
  status: "email-unconfirmed";
  email: string;
}

/** Строка ли поле `email` в теле ответа; иначе тело чужое. */
function readMaskedEmail(body: unknown): string | null {
  if (typeof body === "object" && body !== null && "email" in body) {
    return typeof body.email === "string" ? body.email : null;
  }
  return null;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** Поле `reason` тела отказа; `null` — тела нет или причины в нём нет. */
function readReason(body: unknown): unknown {
  return typeof body === "object" && body !== null && "reason" in body ? body.reason : null;
}

export async function login(credentials: LoginCredentials): Promise<LoginResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("запрос входа не выполнен", error);
    return "unavailable";
  }
  if (response.ok) {
    return "success";
  }
  if (response.status === 401) {
    return "invalid-credentials";
  }
  if (response.status === 429) {
    return "rate-limited";
  }
  if (response.status === 403) {
    const body = await readJson(response);
    const masked = readMaskedEmail(body);
    if (
      masked !== null &&
      typeof body === "object" &&
      body !== null &&
      "reason" in body &&
      body.reason === "email_unconfirmed"
    ) {
      return { status: "email-unconfirmed", email: masked };
    }
  }
  console.error("вход отклонён демоном", response.status);
  return "unavailable";
}

/**
 * Исход запроса выхода. Не бросает и не различает отказ по возврату на
 * форму: демон отвечает `204` на успешный отзыв сессии и `500`, если отзыв
 * не удался, — в обоих случаях и при сетевой ошибке человек всё равно уходит
 * на форму входа (§9, web-login «Демон не ответил на выход»), `logout` лишь
 * сообщает вызывающему, удалась ли попытка, чтобы предупредить о возможно
 * ещё действительной сессии на демоне.
 */
export type LogoutResult = "success" | "failed";

export async function logout(): Promise<LogoutResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/logout`, {
      method: "POST",
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("запрос выхода не выполнен", error);
    return "failed";
  }
  if (response.status === 204) {
    return "success";
  }
  console.error("выход отклонён демоном", response.status);
  return "failed";
}

/**
 * Исходы удаления своей учётной записи (`POST /api/account/delete`, §130,
 * stage9-account-delete Д6). Причина отказа `403` — в теле (`error`):
 * `wrong_password` и `owner` различаются, неизвестная причина — недоступность,
 * а не «неверный пароль» (человек стал бы подбирать то, что ни при чём).
 * `429` — предел попыток, общий со входом. `401` — сессии уже нет. `503`
 * (демон занят проверкой паролей), `5xx` и сеть — недоступность, как у входа.
 */
export type DeleteAccountResult =
  | "deleted"
  | "wrong-password"
  | "owner"
  | "rate-limited"
  | "session-invalid"
  | "unavailable";

export async function deleteAccount(password: string): Promise<DeleteAccountResult> {
  let response: Response;
  try {
    response = await fetch("/api/account/delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("запрос удаления учётной записи не выполнен", error);
    return "unavailable";
  }
  if (response.status === 204) {
    return "deleted";
  }
  if (response.status === 401) {
    return "session-invalid";
  }
  if (response.status === 429) {
    return "rate-limited";
  }
  if (response.status === 403) {
    try {
      const body: unknown = await response.json();
      if (typeof body === "object" && body !== null && "error" in body) {
        if (body.error === "wrong_password") {
          return "wrong-password";
        }
        if (body.error === "owner") {
          return "owner";
        }
      }
    } catch {
      // Нечитаемое тело — как неизвестная причина, ниже.
    }
  }
  console.error("удаление учётной записи отклонено демоном", response.status);
  return "unavailable";
}

/**
 * Флаги регистрации, как их видит демон (`GET /api/auth/signup-options`, без
 * сессии; stage9-signup-invites, Д2). Клиент не спрашивает PostHog: до входа
 * аналитика на локальном адресе выключена, а блокировщик её глушит.
 */
export interface SignupOptions {
  /** Регистрация по ссылке-приглашению (`signup-invite`). */
  invite: boolean;
  /** Открытая регистрация без ссылки (`signup-open`). */
  open: boolean;
  /**
   * Задан ли у демона почтовый сервис (stage9-email-confirmation, Р12). Когда
   * нет, `invite` и `open` демон уже отдаёт `false`.
   */
  email: boolean;
  /**
   * Ключ сайта Cloudflare Turnstile для виджета открытой регистрации
   * (stage9-signup-turnstile, Р3); `null` — открытая регистрация закрыта.
   */
  turnstile_site_key: string | null;
}

export type SignupOptionsResult =
  | { status: "ok"; options: SignupOptions }
  | { status: "unavailable" };

function isSignupOptions(value: unknown): value is SignupOptions {
  return (
    typeof value === "object" &&
    value !== null &&
    "invite" in value &&
    typeof value.invite === "boolean" &&
    "open" in value &&
    typeof value.open === "boolean" &&
    "email" in value &&
    typeof value.email === "boolean" &&
    "turnstile_site_key" in value &&
    (value.turnstile_site_key === null || typeof value.turnstile_site_key === "string")
  );
}

export async function signupOptions(): Promise<SignupOptionsResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/signup-options`, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (!response.ok) {
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isSignupOptions(body)) {
      return { status: "unavailable" };
    }
    return {
      status: "ok",
      options: {
        invite: body.invite,
        open: body.open,
        email: body.email,
        turnstile_site_key: body.turnstile_site_key,
      },
    };
  } catch {
    return { status: "unavailable" };
  }
}

export interface SignupRequest {
  login: string;
  email: string;
  password: string;
  /** Токен ссылки-приглашения; нет — открытая регистрация. */
  token?: string;
  /** Токен виджета Turnstile; только на открытой регистрации (stage9-signup-turnstile). */
  turnstile_token?: string;
}

/**
 * Запись заведена (`202`, stage9-email-confirmation, Р6): сессии нет, человек
 * подтверждает почту. `email` — маска от демона; `mailUnavailable` — письмо не
 * ушло (`"mail": "unavailable"`), нужен повтор.
 */
export interface SignupCreated {
  status: "created";
  email: string;
  mailUnavailable: boolean;
}

/**
 * Исходы регистрации (`POST /api/auth/signup`, Д5). Недействительная ссылка —
 * один исход для четырёх причин (истекла, использована, отозвана, неизвестна):
 * демон их не различает намеренно. Капча: `422` — проверка не пройдена,
 * `502` — Cloudflare не ответил (stage9-signup-turnstile, Р2). `422` с
 * `reason: "email_mismatch"` — ссылка выдана на другую почту
 * (stage9-billing-owner-bot).
 */
export type SignupResult =
  | SignupCreated
  | "invalid"
  | "disabled"
  | "link-invalid"
  | "login-taken"
  | "email-taken"
  | "email-mismatch"
  | "rate-limited"
  | "busy"
  | "captcha-rejected"
  | "captcha-unavailable"
  | "unavailable";

/**
 * Исходы проверки ссылки-приглашения (`POST /api/auth/invite-status`,
 * stage9-auth-redesign, Д1). Токен — в теле, а не в адресе: строка запроса
 * оседает в журнале доступа обратного прокси. `rate-limited` (`429`) и
 * `unavailable` (сбой сети, `5xx`, чужое тело) — не «ссылка не действует»:
 * проверка лишь подсказка, окончательно ссылку решает отправка (Д3).
 */
export type InviteStatusResult = "valid" | "invalid" | "rate-limited" | "unavailable";

export async function fetchInviteStatus(token: string): Promise<InviteStatusResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/invite-status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("проверка ссылки не выполнена", error);
    return "unavailable";
  }
  if (response.status === 429) {
    return "rate-limited";
  }
  if (!response.ok) {
    console.error("проверка ссылки отклонена демоном", response.status);
    return "unavailable";
  }
  try {
    const body: unknown = await response.json();
    if (typeof body === "object" && body !== null && "valid" in body) {
      if (body.valid === true) {
        return "valid";
      }
      if (body.valid === false) {
        return "invalid";
      }
    }
    return "unavailable";
  } catch {
    return "unavailable";
  }
}

export async function signup(request: SignupRequest): Promise<SignupResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      // Регистрация — до 10 с на сервере (капча до 5 с, письмо до 5 с): общий
      // таймаут 35 с её покрывает.
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("запрос регистрации не выполнен", error);
    return "unavailable";
  }
  if (response.status === 202) {
    const body = await readJson(response);
    const masked = readMaskedEmail(body);
    if (masked === null) {
      console.error("регистрация: ответ без адреса почты");
      return "unavailable";
    }
    const mailUnavailable =
      typeof body === "object" && body !== null && "mail" in body && body.mail === "unavailable";
    return { status: "created", email: masked, mailUnavailable };
  }
  switch (response.status) {
    case 400:
      return "invalid";
    case 403:
      return "disabled";
    case 409:
      return readReason(await readJson(response)) === "email" ? "email-taken" : "login-taken";
    case 410:
      return "link-invalid";
    case 422:
      // Тот же код у ссылки, выданной на другую почту: различает причина в
      // теле (stage9-billing-owner-bot, Д11).
      return readReason(await readJson(response)) === "email_mismatch"
        ? "email-mismatch"
        : "captcha-rejected";
    case 429:
      return "rate-limited";
    case 502:
      return "captcha-unavailable";
    case 503:
      return "busy";
    default:
      console.error("регистрация отклонена демоном", response.status);
      return "unavailable";
  }
}

/**
 * Исходы подтверждения почты (`POST /api/auth/confirm-email`, Р7): `204` —
 * почта подтверждена, сессия не выдаётся и чужая не трогается, `410` — ссылка недействительна (неизвестна, истекла,
 * использована, погашена новой — демон не различает). Других кодов демон не
 * отвечает; чужой код — недоступность.
 */
export type ConfirmEmailResult = "confirmed" | "link-invalid" | "unavailable";

export async function confirmEmail(token: string): Promise<ConfirmEmailResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/confirm-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("подтверждение почты не выполнено", error);
    return "unavailable";
  }
  if (response.status === 204) {
    return "confirmed";
  }
  if (response.status === 410) {
    return "link-invalid";
  }
  console.error("подтверждение почты отклонено демоном", response.status);
  return "unavailable";
}

/**
 * Исходы повтора письма (`POST /api/auth/resend-confirmation`, Р9). `accepted`
 * — `202`: ответ общий при любом исходе, он не говорит, что письмо ушло.
 * Пароль не передаётся.
 */
export type ResendConfirmationResult = "accepted" | "rate-limited" | "unavailable";

export async function resendConfirmation(login: string): Promise<ResendConfirmationResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/resend-confirmation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ login }),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("повтор письма не выполнен", error);
    return "unavailable";
  }
  if (response.status === 202) {
    return "accepted";
  }
  if (response.status === 429) {
    return "rate-limited";
  }
  console.error("повтор письма отклонён демоном", response.status);
  return "unavailable";
}

/**
 * Исходы запроса сброса пароля (`POST /api/auth/request-password-reset`,
 * stage9-password-reset, Р3). `accepted` — `202`: ответ общий при любом
 * исходе и не говорит, зарегистрирована ли почта и ушло ли письмо (§129 п. 2).
 * `429` — предел частоты запросов.
 */
export type RequestPasswordResetResult = "accepted" | "rate-limited" | "unavailable";

export async function requestPasswordReset(email: string): Promise<RequestPasswordResetResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/request-password-reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("запрос сброса пароля не выполнен", error);
    return "unavailable";
  }
  if (response.status === 202) {
    return "accepted";
  }
  if (response.status === 429) {
    return "rate-limited";
  }
  console.error("запрос сброса пароля отклонён демоном", response.status);
  return "unavailable";
}

/**
 * Исходы смены пароля (`POST /api/auth/reset-password`, Р4): `204` без тела и
 * без cookie — человек входит заново; `410` — ссылка недействительна (демон
 * причин не различает); `503` — демон занят хешированием, как при входе.
 * Остальное, включая `400` (пустой пароль, клиент его не шлёт), — недоступность.
 */
export type ResetPasswordResult = "changed" | "link-invalid" | "busy" | "unavailable";

export async function resetPassword(token: string, password: string): Promise<ResetPasswordResult> {
  let response: Response;
  try {
    response = await fetch(`${AUTH_BASE}/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("смена пароля не выполнена", error);
    return "unavailable";
  }
  if (response.status === 204) {
    return "changed";
  }
  if (response.status === 410) {
    return "link-invalid";
  }
  if (response.status === 503) {
    return "busy";
  }
  console.error("смена пароля отклонена демоном", response.status);
  return "unavailable";
}
